import React from 'react';
import PropTypes from 'prop-types';
import { LuExternalLink } from 'react-icons/lu';
import { formatInZone } from './reminderFormat';

// The items one recipient's email would list (backend Reminders::Base PREVIEW ITEM),
// most urgent first — what the admin checks before switching a reminder on. Links
// open in a new tab so the settings page stays where it is.

const PRIORITY = {
  high: { label: 'High', cls: 'tw-bg-red-50 tw-text-red-700' },
  medium: { label: 'Medium', cls: 'tw-bg-amber-50 tw-text-amber-700' },
  low: { label: 'Low', cls: 'tw-bg-slate-100 tw-text-slate-600' },
};

// "3 d", "5 h", "20 min" — how long the item has waited.
export const waitedFor = (since, now = Date.now()) => {
  if (!since) return '—';
  const minutes = Math.max(0, Math.floor((now - new Date(since).getTime()) / 60000));
  if (minutes >= 24 * 60) return `${Math.floor(minutes / (24 * 60))} d`;
  if (minutes >= 60) return `${Math.floor(minutes / 60)} h`;
  return `${minutes} min`;
};

const dueText = (date) => new Date(`${date}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });

const ReminderItemList = ({ items, moreItems, timeZone }) => (
  <ul className="tw-m-0 tw-p-0 tw-list-none tw-divide-y tw-divide-slate-100">
    {items.map((item) => {
      const priority = PRIORITY[item.priority];
      return (
        <li key={item.key} className="tw-flex tw-flex-wrap tw-items-start tw-gap-x-4 tw-gap-y-1 tw-py-2">
          <div className="tw-min-w-0 tw-flex-1 tw-basis-64">
            <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
              {item.href ? (
                <a href={item.href} target="_blank" rel="noopener noreferrer"
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[13px] tw-font-semibold tw-text-primary-700 hover:tw-underline">
                  {item.label}<LuExternalLink size={11} aria-hidden="true" />
                </a>
              ) : <span className="tw-text-[13px] tw-font-semibold tw-text-slate-800">{item.label}</span>}
              {priority && <span className={`tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-uppercase ${priority.cls}`}>{priority.label}</span>}
              {item.title && <span className="tw-text-[13px] tw-text-slate-700 tw-truncate">{item.title}</span>}
            </div>
            <div className="tw-text-[12px] tw-text-slate-500">{item.step}</div>
            {item.note && <div className="tw-text-[12px] tw-text-red-700">{item.note}</div>}
          </div>
          {item.amount && <div className="tw-text-[13px] tw-font-semibold tw-tabular-nums tw-text-slate-800 tw-whitespace-nowrap">{item.amount}</div>}
          <div className="tw-text-[12px] tw-text-slate-500 tw-whitespace-nowrap tw-text-right tw-w-40">
            <div title={item.waiting_since ? formatInZone(item.waiting_since, timeZone, true) : undefined}>
              Waiting {waitedFor(item.waiting_since)}
            </div>
            {item.due_date && (
              <div className={item.overdue ? 'tw-text-red-700 tw-font-semibold' : ''}>
                Due {dueText(item.due_date)}{item.overdue ? ' · overdue' : ''}
              </div>
            )}
          </div>
        </li>
      );
    })}
    {moreItems > 0 && (
      <li className="tw-py-2 tw-text-[12px] tw-text-slate-500">… and {moreItems} more — the email lists them all.</li>
    )}
  </ul>
);

ReminderItemList.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    href: PropTypes.string,
    title: PropTypes.string,
    amount: PropTypes.string,
    priority: PropTypes.string,
    step: PropTypes.string,
    waiting_since: PropTypes.string,
    due_date: PropTypes.string,
    overdue: PropTypes.bool,
    note: PropTypes.string,
  })).isRequired,
  moreItems: PropTypes.number,
  timeZone: PropTypes.string.isRequired,
};

export default ReminderItemList;
